/**
 * The handshake between `/login` (apps/web) and the standalone issue-report
 * extension (`apps/issue-report-extension`).
 *
 * Unlike `apps/extension`'s bridge, this extension holds a session of its own
 * — it has no Floto tab to hand work to, so it can report an issue with none
 * open. The handshake exists to get that session into the extension in the
 * first place:
 *
 *   extension  --chrome.tabs.create(`${origin}/login?source=extension&extId=`)-->
 *   web app    --chrome.runtime.sendMessage(extId, EXT_AUTH_SUCCESS)-------->
 *   extension  --chrome.storage.local.set-------------------------------->
 *
 * `extId` rides in the URL itself rather than being hardcoded on the web
 * side: a dev's unpacked extension and the Chrome Web Store build have
 * different ids, and the web app has no other way to learn which one asked.
 *
 * Ships as TypeScript source (see this package's `exports`), like
 * `extension-bridge` — it has a runtime value (`EXT_AUTH_MESSAGE_SOURCE`) and
 * both consumers are bundlers, not Node.
 */
export const EXT_AUTH_MESSAGE_SOURCE = 'floto-issue-report-extension';

/** The extension answering it is here and listening, sent over
 *  `externally_connectable` once the login page's script has loaded — the
 *  extension itself cannot announce first, since it has no page to send to
 *  until this one opens. */
export interface ExtAuthPingMessage {
  source: typeof EXT_AUTH_MESSAGE_SOURCE;
  type: 'EXT_AUTH_PING';
}

/** The web app handing back a signed-in session, once one exists. */
export interface ExtAuthSuccessMessage {
  source: typeof EXT_AUTH_MESSAGE_SOURCE;
  type: 'EXT_AUTH_SUCCESS';
  sessionToken: string;
  user: {
    uid: string;
    email: string | null;
    displayName: string | null;
  };
  /**
   * The team the user had selected in `useScopeStore` at the moment they
   * clicked "Sign in" — absent for personal scope, same convention
   * `apiFetch` (`apps/web/src/api/client.ts`) already follows: no header at
   * all, not an empty string, is what the API reads as "act as my own
   * account". Without this, every app the extension resolves
   * (`appIdFor(scopeKeyOf(user), origin)` — the scope is part of the hash)
   * is computed under the caller's *personal* scope regardless of which
   * team's dashboard the user was actually looking at, landing reports on an
   * app nobody watching the team's fix list would ever see.
   *
   * Captured once, at sign-in — this extension has no team switcher of its
   * own, so switching teams later in the web app doesn't retroactively
   * change where a report lands until signing in again.
   */
  teamId?: string | null;
}

export type ExtAuthMessage = ExtAuthPingMessage | ExtAuthSuccessMessage;

function isEnvelope(
  value: unknown,
): value is { source: unknown; type: unknown } {
  return typeof value === 'object' && value !== null;
}

export function isExtAuthSuccessMessage(
  value: unknown,
): value is ExtAuthSuccessMessage {
  if (!isEnvelope(value)) return false;
  return (
    value.source === EXT_AUTH_MESSAGE_SOURCE &&
    value.type === 'EXT_AUTH_SUCCESS' &&
    typeof (value as Partial<ExtAuthSuccessMessage>).sessionToken === 'string'
  );
}
